/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useQuery } from '@tanstack/react-query';

// Lib
import { apiClient } from '@lib/api-client';

// Types
import {
	TParsedUser,
	TApiResponseGetUsersPopulated,
	TApiResponseUserPopulated,
} from '../types';
import { TParams } from '@global-types/index';
import { TApiParams } from '@global-types/app/app';

// Utils
import { formatDate } from '@utils/functions';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getUsers = (
	params: TApiParams
): Promise<TApiResponseGetUsersPopulated> => {
	const { queryParams, sortParams, page, pageSize } =
		params;
	return apiClient.get('/users', {
		params: {
			page: page,
			pageSize: pageSize,
			query: queryParams,
			sort: sortParams,
			populate: ['avatar', 'organization'],
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
export const useUsersApi = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { t } = useTranslation();
	const { data, isFetching, isFetched } = useQuery({
		queryKey: [
			'users',
			{
				page: page,
				size: size,
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],
		queryFn: () =>
			getUsers({
				page: page,
				pageSize: size,
				queryParams: queryParams,
				sortParams: sortParams,
			}),
	});

	const updateList = (
		users: TApiResponseUserPopulated[]
	) =>
		users.reduce(
			(
				acc: TParsedUser[],
				currentItem: TApiResponseUserPopulated
			) => {
				const modifiedItem: any = {
					...currentItem,
					organizationName: currentItem?.organization
						?.name
						? currentItem?.organization?.name
						: NOT_APPLICABLE,
					roleName: currentItem?.role
						? t(`roles.${currentItem?.role}`)
						: NOT_APPLICABLE,
					language: currentItem?.locale?.language
						? currentItem.locale.language
						: NOT_APPLICABLE,
					phoneNumber: currentItem?.phoneNumber
						? currentItem.phoneNumber
						: NOT_APPLICABLE,
					createdAt: formatDate(
						currentItem.createdAt,
						'DD/MM/YYYY'
					),
					updatedAt: formatDate(
						currentItem.updatedAt,
						'DD/MM/YYYY'
					),
					lastLoginAt: currentItem?.lastLoginAt
						? formatDate(
								currentItem.lastLoginAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
				};

				acc.push(modifiedItem);

				return acc;
			},
			[]
		);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
